/**
 * HistoryDrawer.tsx —— 手牌历史 + 复盘（PRD 模块 5 / AC5.2–5.5）
 *
 * 复盘只读**已落库的快照**：赢率不重算（AC4.9），评分沿用行动当时的记录，
 * 因此同一手牌反复打开的结论完全一致。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { HandEvent, HandRecord } from '../core/game';
import { describeHandValue } from '../core/evaluator';
import { formatCardSymbol, type Card } from '../core/cards';
import type { EquitySnapshot } from '../state/equityTypes';
import { actionText, chips, pct1, STREET_CN } from '../state/format';
import { buildScoreDetail, type ScoreRecord } from '../state/scoring';
import type { ScoreToneMode } from '../state/settings';
import { PlayingCard } from './primitives';
import { EquityBadges } from './EquityPanel';

export function HistoryDrawer({
  records,
  snapshots,
  scores,
  humanSeat,
  names,
  revealFolded,
  onRevealFoldedChange,
  tone = 'pro',
}: {
  records: readonly HandRecord[];
  snapshots: Record<number, Partial<Record<'preflop' | 'flop' | 'turn' | 'river', EquitySnapshot>>>;
  scores: readonly ScoreRecord[];
  humanSeat: number;
  names: readonly string[];
  revealFolded: boolean;
  onRevealFoldedChange: (v: boolean) => void;
  /** 需求二 FR2-2：复盘详解沿用当前语气模式（新手鼓励 / 专业直述），缺省专业直述。 */
  tone?: ScoreToneMode;
}) {
  const list = useMemo(() => [...records].reverse(), [records]);
  const [selected, setSelected] = useState<number | null>(list[0]?.handNumber ?? null);
  const rec = list.find((r) => r.handNumber === selected) ?? list[0] ?? null;

  if (!list.length) {
    return (
      <div className="hist hist--empty">
        <p className="muted">还没有已完成的手牌。打完第一手后，这里会记录每条街的动作、赢率快照与结算明细。</p>
      </div>
    );
  }

  return (
    <div className="hist">
      <ul className="hist__list" role="listbox" aria-label="手牌列表">
        {list.map((r) => {
          const net = r.net.get(humanSeat) ?? 0;
          return (
            <li key={r.handNumber}>
              <button
                type="button"
                role="option"
                aria-selected={rec?.handNumber === r.handNumber}
                className={`hist__item${rec?.handNumber === r.handNumber ? ' hist__item--on' : ''}`}
                onClick={() => setSelected(r.handNumber)}
              >
                <span className="hist__no num">#{r.handNumber}</span>
                <span className="hist__board">
                  {r.board.length ? r.board.map((c: Card) => formatCardSymbol(c)).join(' ') : '未见翻牌'}
                </span>
                <span className={`hist__net num${net > 0 ? ' pos' : net < 0 ? ' neg' : ''}`}>
                  {net > 0 ? `+${chips(net)}` : chips(net)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {rec ? (
        <div className="hist__detail">
          <ReplayDetail
            rec={rec}
            snaps={snapshots[rec.handNumber] ?? {}}
            scores={scores.filter((s) => s.handNumber === rec.handNumber)}
            humanSeat={humanSeat}
            names={names}
            revealFolded={revealFolded}
            onRevealFoldedChange={onRevealFoldedChange}
            tone={tone}
          />
        </div>
      ) : null}
    </div>
  );
}

const STREETS = ['preflop', 'flop', 'turn', 'river'] as const;

/** 复盘时间轴上的一步（AC5.4：逐动作前进 / 后退）。 */
interface ReplayStep {
  /** 该步属于哪条街（发牌步属于新街）。 */
  readonly street: (typeof STREETS)[number];
  /** 步骤类型：街面翻开 / 一次动作 / 摊牌 / 结算。 */
  readonly kind: 'street' | 'action' | 'showdown' | 'settle';
  /** 面板上的一句话描述，用于「第 k / N 步」的状态播报。 */
  readonly label: string;
  /** 若为动作步，指向 rec.events 里对应的动作事件（用于定位高亮）。 */
  readonly event: Extract<HandEvent, { kind: 'action' }> | null;
}

/**
 * buildSteps —— 把一手牌的事件流摊平成「可逐步走」的时间轴。
 *
 * 只取会改变牌面/局面认知的事件（翻牌、每次动作、摊牌、结算），
 * 盲注与发底牌属于手牌初始状态，不占步（复盘一开始就该看到自己的底牌）。
 * 顺序严格沿用引擎写入 events 的顺序，保证「内容与实际一致」。
 */
function buildSteps(rec: HandRecord, nameOf: (seat: number) => string): ReplayStep[] {
  const steps: ReplayStep[] = [];
  let street: (typeof STREETS)[number] = 'preflop';
  for (const ev of rec.events) {
    if (ev.kind === 'street') {
      street = ev.street;
      if (ev.street === 'preflop') continue; // 翻牌前没有公共牌可翻
      steps.push({
        street,
        kind: 'street',
        label: `${STREET_CN[street]}：${ev.board.map((c) => formatCardSymbol(c)).join(' ')}`,
        event: null,
      });
    } else if (ev.kind === 'action') {
      steps.push({
        street: ev.street,
        kind: 'action',
        label: `${nameOf(ev.effect.seat)} ${actionText(ev.effect)}`,
        event: ev,
      });
    } else if (ev.kind === 'showdown') {
      steps.push({ street, kind: 'showdown', label: '摊牌', event: null });
    } else if (ev.kind === 'settle') {
      steps.push({ street, kind: 'settle', label: '结算', event: null });
    }
  }
  return steps;
}

function ReplayDetail({
  rec,
  snaps,
  scores,
  humanSeat,
  names,
  revealFolded,
  onRevealFoldedChange,
  tone,
}: {
  rec: HandRecord;
  snaps: Partial<Record<'preflop' | 'flop' | 'turn' | 'river', EquitySnapshot>>;
  scores: readonly ScoreRecord[];
  humanSeat: number;
  names: readonly string[];
  revealFolded: boolean;
  onRevealFoldedChange: (v: boolean) => void;
  tone: ScoreToneMode;
}) {
  const nameOf = (seat: number): string => names[seat] ?? `座位 ${seat}`;
  // 名字表按内容（而非引用）参与依赖：父组件每次渲染都可能新建 names 数组，
  // 若直接依赖引用，steps 会每次渲染重建，进而把游标打回末尾（NEW-1 回弹缺陷）。
  const namesKey = names.join('\u0001');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 用 namesKey 代替 names 引用，见上
  const steps = useMemo(() => buildSteps(rec, (seat) => names[seat] ?? `座位 ${seat}`), [rec, namesKey]);

  const total = steps.length;

  /*
   * 游标 = 已走过的步数；null 表示「用户还没选过步数」。
   *
   * QA-BUG-025 一并调整默认起点：改前默认「跟随末尾」（打开即看完整结论），
   * 于是打开复盘直接按 `→` 永远是边界空操作（QA r09 U8 实测 5/5 → 5/5 → 5/5，
   * 判定方向键不驱动游标）。「逐动作回放」的默认起点本就该是**手牌开始**：
   *   - 键盘用户打开复盘按 `→` 立刻逐步前进，0 次 Tab；
   *   - 与「结论不提前泄露 / 街次渐进揭示」的既有设计一致；
   *   - 想直接看结论有「结尾 ⏭」按钮与 End 键一键到底，手牌列表每行也已给出净结果。
   * 仍用 null 而不是 0 做初值，是为了保留「未选择」与「明确选了第 0 步」的区分能力，
   * 任何重渲染都不会再触碰用户选中的步数（NEW-1 / r08c V1 游标稳定性）。
   */
  const [cursor, setCursor] = useState<number | null>(null);
  // 只有**真正切换手牌**时才归位（渲染期派生 state，官方推荐写法，不产生额外一帧）。
  const [shownHand, setShownHand] = useState(rec.handNumber);
  if (shownHand !== rec.handNumber) {
    setShownHand(rec.handNumber);
    setCursor(null);
  }
  const at = Math.min(Math.max(cursor ?? 0, 0), total);
  const played = (i: number): boolean => i < at;
  const current = at > 0 ? steps[at - 1] : null;
  const stepIndexOf = (ev: HandEvent): number => steps.findIndex((s) => s.event === ev);
  const streetReached = (st: (typeof STREETS)[number]): boolean => {
    if (st === 'preflop') return true;
    const i = steps.findIndex((s) => s.kind === 'street' && s.street === st);
    return i >= 0 && played(i);
  };
  const kindPlayed = (kind: ReplayStep['kind']): boolean => {
    const i = steps.findIndex((s) => s.kind === kind);
    return i >= 0 && played(i);
  };

  /**
   * QA-BUG-025 根因整改：方向键从「必须先 Tab 到步进区」改为**抽屉级快捷键**。
   *
   * 改前 `←/→` 只挂在 `.rp__stepper` 的 React `onKeyDown` 上：打开复盘后焦点在
   * 「复盘」按钮或列表项上，直接按方向键毫无反应（QA r09 U8 arrowWorks=false），
   * 键盘用户必须先摸到步进区才能逐步走牌。
   *
   * 现在改成 window 级监听，边界三条（保证不抢别人的方向键，也不破坏 r08c V1 游标稳定性）：
   *   ① 复盘面板挂载期间才注册（抽屉关闭即注销）；
   *   ② 焦点在文本框 / 下拉 / 滑杆 / 可编辑区时让位（金额输入、滑杆仍归它们自己）；
   *   ③ 只处理无修饰键的 ←/→/Home/End，且**只写游标**，不触碰手牌选择与任何引擎状态 ——
   *      游标仍是「用户明确选择」的语义，重渲染不会把它打回末尾。
   *   行动区快捷键在抽屉打开时本就由 overlayOpen() 让位，两者不会打架。
   */
  const stepBy = useCallback(
    (delta: number) => setCursor((c) => Math.min(total, Math.max(0, Math.min(c ?? 0, total) + delta))),
    [total],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;
      if (t?.getAttribute('role') === 'slider') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        stepBy(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        stepBy(1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setCursor(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setCursor(total);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stepBy, total]);

  const byStreet = new Map<string, HandEvent[]>();
  for (const ev of rec.events) {
    if (ev.kind !== 'action') continue;
    const arr = byStreet.get(ev.street) ?? [];
    arr.push(ev);
    byStreet.set(ev.street, arr);
  }
  const boardAt: Record<string, readonly Card[]> = { preflop: [] };
  for (const ev of rec.events) if (ev.kind === 'street') boardAt[ev.street] = ev.board;

  const deals = rec.events.filter((e): e is Extract<HandEvent, { kind: 'deal' }> => e.kind === 'deal');
  const myDeal = deals.find((d) => d.seat === humanSeat);
  const showdown = rec.events.find((e): e is Extract<HandEvent, { kind: 'showdown' }> => e.kind === 'showdown');
  const foldedSeats = new Set<number>();
  for (const ev of rec.events) if (ev.kind === 'action' && ev.effect.type === 'fold') foldedSeats.add(ev.effect.seat);

  return (
    <>
      <header className="rp__head">
        <h3 className="rp__title">
          第 <span className="num">{rec.handNumber}</span> 手复盘
        </h3>
        <label className="rp__toggle">
          <input type="checkbox" checked={revealFolded} onChange={(e) => onRevealFoldedChange(e.target.checked)} />
          显示弃牌者底牌
        </label>
      </header>

      <div className="rp__hole">
        <span className="rp__label">你的底牌</span>
        {myDeal ? (
          <>
            <PlayingCard card={myDeal.cards[0]} size="md" />
            <PlayingCard card={myDeal.cards[1]} size="md" />
          </>
        ) : (
          <span className="muted">本手未发到你</span>
        )}
      </div>

      {snaps && Object.keys(snaps).length ? <EquityBadges compact /> : null}

      {/* 逐动作回放控件（AC5.4）：整手内容按时间轴逐步揭示，可前进 / 后退 */}
      {/* 方向键由抽屉级监听统一处理（见上方 useEffect），这里不再重复挂 onKeyDown，避免一次按键走两步 */}
      <div className="rp__stepper" role="group" aria-label="逐动作回放" aria-keyshortcuts="ArrowLeft ArrowRight Home End">
        <button type="button" className="btn" disabled={at === 0} onClick={() => setCursor(0)} aria-label="回到手牌开始">
          ⏮ 开头
        </button>
        <button type="button" className="btn" disabled={at === 0} onClick={() => setCursor(at - 1)}>
          ← 上一步
        </button>
        <span className="rp__stepstate" aria-live="polite">
          第 <b className="num">{at}</b> / <span className="num">{total}</span> 步
          {current ? <span className="rp__stepnow">{current.label}</span> : <span className="rp__stepnow">手牌开始（仅底牌与盲注）</span>}
        </span>
        <button type="button" className="btn" disabled={at === total} onClick={() => setCursor(at + 1)}>
          下一步 →
        </button>
        <button type="button" className="btn" disabled={at === total} onClick={() => setCursor(total)} aria-label="跳到手牌结束">
          结尾 ⏭
        </button>
      </div>

      {STREETS.map((st) => {
        const actions = byStreet.get(st) ?? [];
        const board = boardAt[st] ?? (st === 'preflop' ? [] : undefined);
        if (!actions.length && board === undefined) return null;
        if (!streetReached(st)) return null; // 游标还没走到这条街：公共牌不提前泄露
        const snap = snaps[st];
        return (
          <section key={st} className="rp__street">
            <div className="rp__street-head">
              <h4 className="rp__street-title">{STREET_CN[st]}</h4>
              <span className="rp__street-board">
                {(board ?? []).map((c) => (
                  <PlayingCard key={c} card={c} size="sm" />
                ))}
              </span>
              <span className="rp__street-eq num">
                {snap ? `${pct1(snap.equity)}` : '—'}
                <span className="rp__street-eqlabel">底池份额期望</span>
              </span>
            </div>
            <ol className="rp__actions">
              {actions.map((ev, i) => {
                if (ev.kind !== 'action') return null;
                const idx = stepIndexOf(ev);
                if (!played(idx)) return null; // 未走到的动作不显示
                const isNow = idx === at - 1;
                return (
                  <li key={i} className={isNow ? 'rp__act-item rp__act-item--now' : 'rp__act-item'} aria-current={isNow ? 'step' : undefined}>
                    <span className="rp__seat">{nameOf(ev.effect.seat)}</span>
                    <span className="rp__act">{actionText(ev.effect)}</span>
                    <span className="rp__pot num">池 {chips(ev.potBefore)}</span>
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}

      {showdown && kindPlayed('showdown') ? (
        <section className="rp__street">
          <h4 className="rp__street-title">摊牌</h4>
          <ul className="rp__reveals">
            {showdown.reveals.map((r) => (
              <li key={r.seat}>
                <span className="rp__seat">{nameOf(r.seat)}</span>
                <PlayingCard card={r.hole[0]} size="sm" />
                <PlayingCard card={r.hole[1]} size="sm" />
                <span className="rp__made">{describeHandValue(r.value).text}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {revealFolded && foldedSeats.size && kindPlayed('settle') ? (
        <section className="rp__street">
          <h4 className="rp__street-title">弃牌者底牌</h4>
          <ul className="rp__reveals">
            {deals
              .filter((d) => foldedSeats.has(d.seat))
              .map((d) => (
                <li key={d.seat}>
                  <span className="rp__seat">{nameOf(d.seat)}</span>
                  <PlayingCard card={d.cards[0]} size="sm" dimmed />
                  <PlayingCard card={d.cards[1]} size="sm" dimmed />
                </li>
              ))}
          </ul>
        </section>
      ) : null}

      {rec.settlement && kindPlayed('settle') ? (
        <section className="rp__street">
          <h4 className="rp__street-title">结算</h4>
          <ul className="rp__awards">
            {rec.settlement.awards.map((a, i) => (
              <li key={i}>
                <span>{a.potIndex === 0 ? '主池' : `边池 ${a.potIndex}`}</span>
                <span className="num">{chips(a.amount)}</span>
                <span>→ {a.winners.map(nameOf).join('、')}</span>
                {a.remainder > 0 ? <span className="muted">零头 {a.remainder} 给 {a.remainderSeats.map(nameOf).join('、')}</span> : null}
              </li>
            ))}
          </ul>
          <p className="rp__net">
            你的本手净收益{' '}
            <b className={`num${(rec.net.get(humanSeat) ?? 0) > 0 ? ' pos' : (rec.net.get(humanSeat) ?? 0) < 0 ? ' neg' : ''}`}>
              {fmtNet(rec.net.get(humanSeat) ?? 0)}
            </b>
          </p>
        </section>
      ) : null}

      {scores.length && kindPlayed('settle') ? (
        <section className="rp__street">
          <h4 className="rp__street-title">本手决策评分</h4>
          <ul className="rp__scores">
            {scores.map((s, i) => {
              // R4 + 需求二：复盘用与实时提示同一口径的详解（按当前语气模式产出段落）。
              const d = buildScoreDetail(s, tone);
              return (
                <li key={i} className={`rp__score tier--${s.tier} rp__score--tone-${d.toneClass}`}>
                  <div className="rp__score-head">
                    <span className="rp__score-when">
                      {s.street} · {s.actionText}
                    </span>
                    <span className={`tier tier--${s.tier}`}>{d.tierName}</span>
                    <span className="num">EV 差 {(s.evDiffRatio * 100).toFixed(1)}% 底池</span>
                  </div>
                  <p className="rp__score-seg">{d.actionLine}</p>
                  {d.segs.map((seg, j) => (
                    <p
                      key={j}
                      className={`rp__score-seg${seg.startsWith('更优打法') ? ' rp__score-seg--best' : ''}${seg.startsWith('ⓘ') ? ' rp__score-seg--note' : ''}`}
                    >
                      {seg}
                    </p>
                  ))}
                </li>
              );
            })}
          </ul>
          <p className="muted">
            评分基于该街的精确赢率与底池赔率，在动作发生后给出；行动过程中不提供任何提示。
          </p>
        </section>
      ) : null}
    </>
  );
}

function fmtNet(n: number): string {
  return n > 0 ? `+${chips(n)}` : chips(n);
}
